import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getFilteredSurveys, getCompletedQualifiedSurveys } from '../../actions/index.js';
import NavBar from '../../components/NavBar';
import { Footer } from '../Footer/Footer';
import { useLocalStorage } from '../../CustomHooks/useLocalStorage';
import { TOKEN } from '../../constants';

import './surveyAdminView.css';

export const SurveyAdminView = () => {
  const dispatch = useDispatch();
  const [value] = useLocalStorage(TOKEN);
  const [isAdmin, setIsAdmin] = useState(null);
  const [expandedUsers, setExpandedUsers] = useState({});
  const [completedSurveys, setCompletedSurveys] = useState([]);

  useEffect(() => {
    if (value) {
      if (!localStorage.getItem(TOKEN)) {
        return;
      }
      const currentUser = JSON.parse(atob(localStorage.getItem(TOKEN)?.split('.')?.[1]));
      setIsAdmin(currentUser.role === 'admin');
    }
  }, [value]);

  useEffect(() => {
    const fetchSurveys = async () => {
      if (isAdmin && value) {
        try {
          await dispatch(getFilteredSurveys({ parms: {}, token: value }));
          const result = await dispatch(
            getCompletedQualifiedSurveys({ params: { qualified: true, days: 7 }, token: value }),
          );
          setCompletedSurveys(result?.payload?.data || []);
        } catch (error) {
          console.error('Error fetching surveys:', error);
        }
      }
    };

    fetchSurveys();
  }, [isAdmin, value, dispatch]);

  const surveyData = useSelector((state) => state.surveyReducer?.surveys || []);

  const surveys = surveyData.surveys ? Object.values(surveyData.surveys) : [];

  const groupedSurveys = {};
  surveys.forEach((survey) => {
    const userId = survey.usersId ?? null;
    if (!groupedSurveys[userId]) {
      groupedSurveys[userId] = [];
    }
    groupedSurveys[userId].push(survey);
  });

  const combinedSurveys = Object.entries(groupedSurveys).map(([usersId, surveys]) => {
    let latestSurvey = surveys[0];

    for (let i = 1; i < surveys.length; i++) {
      const current = surveys[i];
      if (new Date(current.createdAt) > new Date(latestSurvey.createdAt)) {
        latestSurvey = current;
      }
    }

    return { ...latestSurvey, usersId: usersId ?? null };
  });

  const toggleUserExpansion = (userId) => {
    setExpandedUsers((prev) => ({
      ...prev,
      [userId]: !prev[userId],
    }));
  };

  const VISIBLE_FIELDS = ['usersId', 'qualifiedGiftCard'];
  const surveyFields = ['Survey Type', 'Date', 'Completed', 'Skipped'];

  // Helper function to get nested survey data values
  const getSurveyValue = (survey, field) => {
    switch (field) {
      case 'usersId':
        return survey.usersId ?? 'null';
      case 'qualifiedGiftCard': {
        const user = completedSurveys?.find((u) => u?.usersId === survey?.usersId);
        return user?.allSurveysCompleted ? 'Yes' : 'No';
      }
      default:
        return '';
    }
  };

  return (
    <>
      <div>
        <NavBar currentPath="/survey" />
        <div className="survey-container">
          {isAdmin ? (
            <div>
              <h1 className="survey-header">Survey Viewer</h1>

              {surveyData.totalCount === 0 ? (
                <div className="no-surveys">
                  <p>No surveys found.</p>
                </div>
              ) : (
                <div>
                  <p className="survey-header">Total Number of Surveys: {surveyData.totalCount}</p>
                  <table className="survey-table">
                    <thead>
                      <tr>
                        {VISIBLE_FIELDS.map((field) => (
                          <th>
                            {field === 'qualifiedGiftCard' ? 'Qualified Gift Card' : 'User Id'}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {combinedSurveys.map((survey) => (
                        <>
                          <tr key={survey.id} onClick={() => toggleUserExpansion(survey.usersId)}>
                            {VISIBLE_FIELDS.map((field) => (
                              <td key={field}>{getSurveyValue(survey, field)}</td>
                            ))}
                          </tr>
                          {expandedUsers[survey.usersId] && (
                            <tr key={`${survey.usersId}-details`}>
                              <td colSpan={VISIBLE_FIELDS.length}>
                                <table className="sub-table">
                                  <thead>
                                    <tr>
                                      {surveyFields.map((headers) => (
                                        <th> {headers} </th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {groupedSurveys[survey.usersId]?.map((s) => (
                                      <tr key={s.id}>
                                        <td>{s.surveyType}</td>
                                        <td>{new Date(s.createdAt).toLocaleDateString()}</td>
                                        <td>
                                          {s.surveyData &&
                                          Object.values(s.surveyData).some((cat) => cat.completed)
                                            ? 'Yes'
                                            : 'No'}
                                        </td>
                                        <td>
                                          {s.surveyData &&
                                          Object.values(s.surveyData).some((cat) => cat.skipped)
                                            ? 'Yes'
                                            : 'No'}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          )}
                        </>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <h2>🚫 Access Denied</h2>
              <p>Only admins can view this page.</p>
            </div>
          )}
        </div>
      </div>
      <Footer currentPath={'/survey'} />
    </>
  );
};
